<template>
    <div>
        <div
            v-if="options.datatableWrapper || isUndefined(options.datatableWrapper)"
            class="datatable-shell"
        >
            <div
                class="datatable-toolbar"
                :class="{
                    'datatable-toolbar--filters-only': !(options.showSearch || isUndefined(options.showSearch)),
                    'datatable-toolbar--search-only': !hasToolbarActions
                }"
            >
                <div class="datatable-toolbar__bar">
                    <div
                        v-if="hasToolbarActions"
                        class="datatable-toolbar__actions filters-wrapper"
                    >
                        <manage-columns
                            v-if="((options.showManageColumn || isUndefined(options.showManageColumn)) && !isCardView)"
                            :manage-option="columns"
                            :init-option="initColumns"
                            @get-columns-options="getColumnsOptions"
                        />

                        <div v-if="cardView" class="single-filter">
                            <button
                                v-if="!isCardView"
                                :key="'card-view'"
                                class="btn btn-filter btn-list-grid"
                                type="button"
                                @click.prevent="toggleDatatableView">
                                <app-icon :name="options.cardViewIcon ? options.cardViewIcon : 'grid'"/>
                            </button>
                            <button
                                v-else
                                :key="'list-view'"
                                class="btn btn-filter btn-list-grid"
                                type="button"
                                @click.prevent="toggleDatatableView">
                                <app-icon name="align-justify"/>
                            </button>
                        </div>

                        <!--Filter save-->

                        <saved-filter-list
                            v-if="options.enableSaveFilter"
                            :table-id="id"
                            :filter-keys="filterKeys"
                        />

                        <button
                            v-if="hasVisibleFilters"
                            class="btn btn-filter datatable-filter-launcher"
                            :class="{ applied: isFiltersOpen || clearFilterVisible }"
                            type="button"
                            @click.prevent="toggleFilters"
                        >
                            <app-icon :name="'filter'"/>
                            <span>{{ $t('filters') }}</span>
                            <span
                                v-if="activeFilterCount"
                                class="datatable-filter-launcher__count"
                            >
                                {{ activeFilterCount }}
                            </span>
                        </button>

                        <button
                            v-if="clearFilterVisible"
                            class="btn btn-filter datatable-filter-reset"
                            type="button"
                            @click.prevent="clearAllFiltersFromToolbar"
                        >
                            <app-icon :name="'x'"/>
                            <span>{{ $t('clear_all_filters') }}</span>
                        </button>
                    </div>

                    <div
                        v-if="options.showSearch || isUndefined(options.showSearch)"
                        class="datatable-toolbar__search"
                    >
                        <div class="single-filter single-search-wrapper">
                            <app-search @input="getSearchValue"/>
                        </div>
                    </div>
                </div>

                <transition name="datatable-filter-panel">
                    <div
                        v-if="hasVisibleFilters && isFiltersOpen"
                        class="datatable-toolbar__panel-shell"
                        :class="{ 'datatable-toolbar__panel-shell--mobile': isMobileViewport }"
                    >
                        <div class="datatable-toolbar__panel">
                            <div class="datatable-toolbar__panel-header">
                                <strong class="datatable-toolbar__panel-title">
                                    {{ $t('filters') }}
                                </strong>

                                <button
                                    type="button"
                                    class="btn btn-filter datatable-toolbar__panel-close"
                                    @click.prevent="toggleFilters"
                                >
                                    <app-icon :name="'x'"/>
                                    <span>{{ $t('close') }}</span>
                                </button>
                            </div>

                            <div class="datatable-toolbar__panel-filters filters-wrapper">
                                <app-filter
                                    :table-id="id"
                                    :filters="options.filters"
                                    :options="options"
                                    @get-values="getFilterValues"
                                />
                            </div>

                            <button
                                type="button"
                                class="btn btn-primary btn-with-shadow d-sm-none btn-close-filter-wrapper d-flex justify-content-center align-items-center"
                                @click="toggleFilters"
                            >
                                {{ $t('close') }}
                            </button>
                        </div>
                    </div>
                </transition>

                <div
                    v-if="activeFiltersSummary.length"
                    class="datatable-toolbar__summary"
                >
                    <span
                        v-for="filter in activeFiltersSummary"
                        :key="filter.key"
                        class="datatable-active-filter-chip"
                    >
                        {{ filter.title }}
                    </span>
                </div>
            </div>

            <table-with-wrapper
                :id="id"
                :options="options"
                :row-density="rowDensity"
                :columns="columns"
                :card-view="isCardView"
                :filtered-data="filterForTable"
                :search-value="searchForTable"
                :clear-filter-visible="clearFilterVisible"
                @action="getAction"
                @afterClearFilter="reloadAfterClearFilter"
                :key="'table-with-wrapper'+id"
            />
        </div>

        <table-without-wrapper
            v-else
            :id="id"
            :options="options"
            :row-density="rowDensity"
            :columns="columns"
            :card-view="isCardView"
            :filtered-data="filterForTable"
            :search-value="searchForTable"
            :clear-filter-visible="clearFilterVisible"
            @action="getAction"
            @afterClearFilter="reloadAfterClearFilter"
            :key="'table-without-wrapper'+id"
        />
    </div>
</template>

<script>
import TableWithWrapper from "./TableWithWrapper";
import TableWithoutWrapper from "./TableWithoutWrapper";
import ManageColumns from "./ManageColumns";
import SavedFilterList from "./helpers/SavedFilterList";
import coreLibrary from '../../helpers/CoreLibrary';
import moment from "moment/moment";
import TableQueryMixin from "./mixin/TableQueryMixin";

export default {
    name: "AppTable",
    components: {
        TableWithWrapper,
        ManageColumns,
        TableWithoutWrapper,
        SavedFilterList
    },
    extends: coreLibrary,
    mixins: [TableQueryMixin],
    props: {
        id: {
            required: true,
            type: String,
        },
        options: {
            required: true,
            type: Object
        },
        rowDensity: {
            type: String,
            default: null
        },
        filteredData: Object,
        search: String,

        // for card view
        cardView: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            filterValues: {},
            columns: [],
            isFiltersOpen: false,
            isMobileViewport: false,
            searchValue: '',
            isCardView: false,
            clearFilterVisible: false,
            filterKeys: [],
        }
    },
    watch: {
        initColumns: function (data) {
            this.columns = data;
        }
    },
    created() {
        this.columns = this.preparedColumn();
        this.isCardView = this.cardView;
        setTimeout(() => {
            this.setInitFilterValues();
        })
    },
    beforeMount() {
        if (this.options.enableCookie || this.isUndefined(this.options.enableCookie))
            this.getCookies();
    },
    mounted() {
        this.handleToolbarResize();
        window.addEventListener('resize', this.handleToolbarResize);
        this.initializeDropdownLayering();
        this.setRows();
    },
    beforeDestroy() {
        window.removeEventListener('resize', this.handleToolbarResize);
        this.destroyDropdownLayering();
    },
    computed: {
        filterForTable() {
            let condition = this.options.datatableWrapper || this.isUndefined(this.options.datatableWrapper);
            return condition ? this.filterValues : this.filteredData;
        },
        searchForTable() {
            let condition = this.options.datatableWrapper || this.isUndefined(this.options.datatableWrapper);
            return condition ? this.searchValue : this.search;
        },
        initColumns() {
            return this.preparedColumn();
        },
        showFilterToolbar() {
            return this.options.showFilter || this.isUndefined(this.options.showFilter);
        },
        availableFilters() {
            if (!this.showFilterToolbar || this.isUndefined(this.options.filters) || !Array.isArray(this.options.filters)) {
                return [];
            }

            return this.options.filters.filter(filter => this.isUndefined(filter.permission) || filter.permission);
        },
        hasVisibleFilters() {
            return this.availableFilters.length > 0;
        },
        hasToolbarActions() {
            return this.showFilterToolbar && (
                this.hasVisibleFilters
                || this.options.enableSaveFilter
                || ((this.options.showManageColumn || this.isUndefined(this.options.showManageColumn)) && !this.isCardView)
                || this.cardView
                || this.clearFilterVisible
            );
        },
        activeFiltersSummary() {
            return this.availableFilters
                .map(filter => {
                    const key = this.normalizeFilterKey(filter.key);

                    return {
                        key,
                        title: filter.title,
                        value: this.filterValues[key]
                    };
                })
                .filter(filter => this.hasMeaningfulFilterValue(filter.value));
        },
        activeFilterCount() {
            return this.activeFiltersSummary.length;
        }
    },
    methods: {
        /**
         * Set filterValues if set any active or initial values
         * */
        setInitFilterValues() {
            let condition = (this.options.datatableWrapper || this.isUndefined(this.options.datatableWrapper))
                && this.options.filters;
            if (condition) {
                // filtersWP means FilterWithPermission
                let filtersWP = this.options.filters.filter(f => (this.isUndefined(f.permission) || f.permission)),
                    initFilters = filtersWP.filter(item => item.initValue || item.active),
                    filter = {};
                this.filterKeys = filtersWP.map(el => _.snakeCase(_.lowerCase(el.key)));
                initFilters.forEach((item) => {
                    let key = _.snakeCase(_.lowerCase(item.key)),
                        value = item.initValue || item.active;
                    this.filterKeys.push(key);
                    if (item.type === 'date') filter[key] = moment(new Date(value)).format('YYYY-MM-DD').toString();
                    else if (item.type === 'checkbox' || item.type === 'multi-select-filter') {
                        filter[key] = typeof value === 'string' ? value : (Array.isArray(value) ? value.toString() : '');
                    } else filter[key] = value;
                })
                this.filterValues = filter;
                this.checkClearFilterVisibility(filter);
            }
        },
        normalizeFilterKey(key) {
            return _.snakeCase(_.lowerCase(key));
        },
        checkClearFilterVisibility(filterValues) {
            let activeFilters = Object.values(filterValues).filter(item => {
                if (typeof item === 'object') return !this.isEmpty(item)
                if (typeof item === 'string') return String(item) !== ''
                if (typeof item === 'number') return Boolean(item)
                if (typeof item === 'boolean') return item
                return false
            })
            this.clearFilterVisible = activeFilters.length !== 0;
        },

        setDefaultFilterValues() {

        },

        /**
         * Reload after clearing all filter values
         * */
        reloadAfterClearFilter() {
            this.filterValues = {};
            this.setDefaultFilterValues();
            this.clearFilterVisible = false;
            this.isFiltersOpen = false;
            this.$emit('getFilteredValues', this.filterValues);
            setTimeout(() => {
                this.$hub.$emit('reload-' + this.id);
            });
        },
        clearAllFiltersFromToolbar() {
            this.reloadAfterClearFilter();
            this.$hub.$emit('clearAllFilter-' + this.id);
        },

        /**
         * Emit from manage-columns
         * */
        getColumnsOptions(value) {
            let data = _.cloneDeep(value);
            this.columns = data;
            if (this.options.enableCookie || this.isUndefined(this.options.enableCookie))
                this.setCookies(data);
        },

        /**
         * Emit from filter
         * */
        getFilterValues(values) {
            this.filterValues = {...this.filterValues, ...values};
            this.checkClearFilterVisibility(this.filterValues);
            this.$emit('getFilteredValues', this.filterValues);
            setTimeout(() => {
                this.$hub.$emit('reload-' + this.id);
            });
        },

        /**
         * Emit from search
         * */
        getSearchValue(value) {
            this.searchValue = value;
            setTimeout(() => {
                this.$hub.$emit('reload-' + this.id);
            });
        },
        initializeDropdownLayering() {
            this.dropdownShownHandler = (event) => {
                const dropdown = event.target.closest('.dropdown');
                if (dropdown) {
                    this.positionFloatingDropdown(dropdown);
                }
            };

            this.dropdownHideHandler = (event) => {
                const dropdown = event.target.closest('.dropdown');
                if (!dropdown) {
                    return;
                }

                const menu = dropdown.querySelector('.dropdown-menu');
                if (!menu) {
                    return;
                }

                menu.classList.remove('dropdown-menu--edge-right');
                menu.style.removeProperty('--datatable-dropdown-max-width');
            };

            $(this.$el).on(
                'shown.bs.dropdown',
                '.datatable-toolbar .dropdown, .datatable-meta-bar .dropdown',
                this.dropdownShownHandler
            );

            $(this.$el).on(
                'hide.bs.dropdown',
                '.datatable-toolbar .dropdown, .datatable-meta-bar .dropdown',
                this.dropdownHideHandler
            );
        },
        destroyDropdownLayering() {
            $(this.$el).off(
                'shown.bs.dropdown',
                '.datatable-toolbar .dropdown, .datatable-meta-bar .dropdown',
                this.dropdownShownHandler
            );

            $(this.$el).off(
                'hide.bs.dropdown',
                '.datatable-toolbar .dropdown, .datatable-meta-bar .dropdown',
                this.dropdownHideHandler
            );
        },
        positionFloatingDropdown(dropdown) {
            const menu = dropdown.querySelector('.dropdown-menu');
            if (!menu) {
                return;
            }

            menu.classList.remove('dropdown-menu--edge-right');

            const boundaryElement = dropdown.closest('.datatable-toolbar__panel')
                || dropdown.closest('.datatable-toolbar')
                || dropdown.closest('.datatable-meta-bar'),
                boundaryRect = boundaryElement
                    ? boundaryElement.getBoundingClientRect()
                    : {
                        left: 16,
                        right: window.innerWidth - 16,
                        width: window.innerWidth - 32
                    },
                safeWidth = Math.max(
                    220,
                    Math.floor(Math.min(window.innerWidth - 32, boundaryRect.width - 24))
                );

            menu.style.setProperty('--datatable-dropdown-max-width', `${safeWidth}px`);

            this.$nextTick(() => {
                const menuRect = menu.getBoundingClientRect(),
                    rightLimit = Math.min(boundaryRect.right - 12, window.innerWidth - 16);

                if (menuRect.right > rightLimit) {
                    menu.classList.add('dropdown-menu--edge-right');
                }
            });
        },
        handleToolbarResize() {
            const isMobileViewport = window.innerWidth <= 991;

            if (this.isMobileViewport !== isMobileViewport) {
                this.isFiltersOpen = false;
            }

            this.isMobileViewport = isMobileViewport;
        },
        toggleFilters() {
            if (!this.hasVisibleFilters) {
                return;
            }

            this.isFiltersOpen = !this.isFiltersOpen;
        },
        hasMeaningfulFilterValue(value) {
            if (Array.isArray(value)) {
                return value.length > 0;
            }

            if (value instanceof Date) {
                return true;
            }

            if (typeof value === 'object' && value !== null) {
                return Object.values(value).some(item => this.hasMeaningfulFilterValue(item));
            }

            if (typeof value === 'string') {
                return value.trim() !== '';
            }

            if (typeof value === 'number') {
                return Boolean(value);
            }

            if (typeof value === 'boolean') {
                return value;
            }

            return !this.isUndefined(value) && value !== null;
        },

        /**
         * $emit for action
         * @param rowData
         * @param actionObj
         * @param active
         * */
        getAction(rowData, actionObj, active) {
            this.$emit("action", rowData, actionObj, active);
        },

        /**
         * Send selected rows to parents by event
         * */
        setRows() {
            this.$hub.$on('selectedRowsData-' + this.id, (rows, bulkSelected) => {
                this.$emit('getRows', rows, bulkSelected);
            });
        },

        /**
         * Set columns value in cookies
         * */
        setCookies(values) {
            let name = this.id + '-columns',
                data = JSON.stringify(values);
            AppCookie.set(name, data);
        },

        /**
         * Get columns value from cookies
         * */
        getCookies() {
            let name = this.id + '-columns',
                data = JSON.parse(AppCookie.get(name));
            if (data) {
                data.map((item) => {
                    let actualColumn = this.initColumns.find(column => column.title === item.title);
                    if (actualColumn?.modifier)
                        item.modifier = actualColumn.modifier;
                });
                this.columns = data;
            }
        },

        preparedColumn() {
            return this.options.columns.filter(item => !this.isUndefined(item.title)).map(column => {
                return {
                    ...column,
                    isVisible: column.isVisible !== undefined ? column.isVisible : true
                }
            })
        },

        /**
         * Toggle Data List & Card View
         * */
        toggleDatatableView() {
            this.isCardView = !this.isCardView;
            setTimeout(() => {
                this.$hub.$emit('reload-' + this.id, false);
            });
        }
    }
}
</script>
